import { Injectable } from '@angular/core';
import { SupplierModel } from 'src/app/core/models/supplier.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { SupplierContactModel } from '../../../../core/models/supplier-contact.model';
import { map } from 'rxjs/operators';
import { AddressModel } from '../../../../core/models/address.model';
import { CategoryModel } from '../../../../core/models/category.model';
import { ClientNumberModel } from '../../../../core/models/client-number-model';
import {
  SupplierGouvResultModel,
  SupplierGouvSearchCriteria,
} from '../../../../core/models/supplier-gouv-result.model';
import { AuthService } from '../../../../core/services/auth.service';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { PermissionMessageService } from '../../../../core/services/permission-message.service';

export interface EstablishmentMissingContacts {
  establishmentId: string;
  establishmentLabel: string;
  missingContactTypes: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SupplierService extends BaseService<SupplierModel> {
  defaultSort = 'createdAt';
  sortOrder = 'desc';

  protected _contacts = new BehaviorSubject<SupplierContactModel[]>([] as SupplierContactModel[]);
  readonly contacts$ = this._contacts.asObservable();

  private _missingContactsRefresh = new Subject<void>();
  readonly missingContactsRefresh$ = this._missingContactsRefresh.asObservable();

  triggerMissingContactsRefresh(): void {
    this._missingContactsRefresh.next();
  }

  constructor(
    public http: HttpClient,
    public authService: AuthService,
    private permissionMessageService: PermissionMessageService,
  ) {
    super(http, 'supplier', authService);
  }

  findById(id: string): Observable<ObjectApiInterface<SupplierModel>> {
    return super.findById(id).pipe(
      map(x => {
        // S'assurer que les contacts sont initialisés même si ils sont undefined
        const contacts = this._entity.value?.contacts || [];
        this._contacts.next(contacts);
        return x;
      }),
    );
  }

  findAllSupplierContactById(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = this.sortOrder,
    search?: string,
    filters?: SupplierModel | any,
  ): Observable<ObjectApiInterface<SupplierContactModel[]>> {
    return this.http.get<ObjectApiInterface<SupplierContactModel[]>>(`${this.baseUrlApi}/supplier-contact/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  // Recherche d'entreprises via le proxy back (API Recherche d'Entreprises — data.gouv.fr, PRED-3215).
  searchGouvApi(criteria: SupplierGouvSearchCriteria): Observable<ObjectApiInterface<SupplierGouvResultModel[]>> {
    let params = new HttpParams();
    Object.entries(criteria).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<ObjectApiInterface<SupplierGouvResultModel[]>>(`${this.baseUrlApi}/gouv-api-supplier`, {
      params,
    });
  }

  getContactTypeRelation(_id): Observable<ObjectApiInterface<SupplierModel[]>> {
    return this.http.get<ObjectApiInterface<SupplierModel[]>>(`${this.baseUrlApi}/getContactTypeRelation/${_id}`);
  }

  searchContacts(search: string) {
    // Vérifier que l'entité et les contacts existent
    if (!this._entity.value || !this._entity.value.contacts) {
      this._contacts.next([]);
      return;
    }

    // Si search est fourni et non vide, filtrer les contacts
    if (search && search.trim().length > 0) {
      const searchLower = search.toLowerCase();
      const match = this._entity.value.contacts.filter(x => {
        const email = x.email?.toLowerCase().includes(searchLower);
        const fullName = `${x.firstName} ${x.lastName}`.toLowerCase().includes(searchLower);
        const fullNameReverse = `${x.lastName} ${x.firstName}`.toLowerCase().includes(searchLower);
        const phone = x.phoneNumbers?.some(
          p => p.phoneNumber?.includes(search) || p.displayPhoneNumber?.includes(search),
        );
        return email || fullName || fullNameReverse || phone;
      });
      this._contacts.next(match);
    } else {
      // Si search est vide ou null, afficher tous les contacts
      this._contacts.next(this._entity.value.contacts || []);
    }
  }

  getContacts(supplierId: string): Observable<ObjectApiInterface<SupplierContactModel[]>> {
    return this.http.get<ObjectApiInterface<SupplierContactModel[]>>(`${this.baseUrlApi}/${supplierId}/getContacts`);
  }

  createContact(
    supplierId: string,
    contact: SupplierContactModel,
  ): Observable<ObjectApiInterface<SupplierContactModel>> {
    // Filet unique des mutations de contacts fournisseur : la modale partagée est ouverte
    // depuis devis, commande, contrat et établissement (PRED-3128).
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return EMPTY;
    }
    return this.http
      .post<ObjectApiInterface<SupplierContactModel>>(`${this.baseUrlApi}/${supplierId}/createContact`, contact)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          entity?.contacts?.push(x.data);
          this._entity.next(entity);
          return x;
        }),
      );
  }

  deletingFamillies() {
    return this.http.delete<ObjectApiInterface<SupplierModel>>(`${this.baseUrlApi}/deleteEmptyFamillies`);
  }

  deleteContact(id: string, contactId: string): Observable<ObjectApiInterface<SupplierContactModel>> {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return EMPTY;
    }
    return this.http
      .delete<ObjectApiInterface<SupplierContactModel>>(`${this.baseUrlApi}/${id}/deleteContact/${contactId}`)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const findedIdx = entity.contacts.findIndex(y => y._id === contactId);
          if (findedIdx !== -1) {
            entity.contacts.splice(findedIdx, 1);
            this._entity.next(entity);
          }
          return x;
        }),
      );
  }

  checkDeleteContactImpact(supplierId: string, contactId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrlApi}/${supplierId}/checkDeleteContactImpact/${contactId}`);
  }

  updateContact(id: string, data: any): Observable<ObjectApiInterface<SupplierModel>> {
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.UPDATE_SUPPLIER)) {
      return EMPTY;
    }
    return this.http
      .put<ObjectApiInterface<SupplierModel>>(`${this.baseUrlApi}/${id}/updateContact/${data._id}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  createAddress(supplierId: string, address: AddressModel): Observable<ObjectApiInterface<AddressModel>> {
    return this.http
      .post<ObjectApiInterface<AddressModel>>(`${this.baseUrlApi}/${supplierId}/createAddress`, address)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          entity.addresses.push(x.data);
          this._entity.next(entity);
          return x;
        }),
      );
  }

  deleteAddress(id: string, addressId: string): Observable<ObjectApiInterface<AddressModel>> {
    return this.http
      .delete<ObjectApiInterface<AddressModel>>(`${this.baseUrlApi}/${id}/deleteAddress/${addressId}`)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const findedIdx = entity.addresses.findIndex(y => y._id === addressId);
          if (findedIdx !== -1) {
            entity.addresses.splice(findedIdx, 1);
            this._entity.next(entity);
          }
          return x;
        }),
      );
  }

  updateAddress(id: string, data: any): Observable<ObjectApiInterface<SupplierModel>> {
    return this.http
      .put<ObjectApiInterface<SupplierModel>>(`${this.baseUrlApi}/${id}/updateAddress/${data._id}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  createFamille(supplierId: string, famille: CategoryModel): Observable<ObjectApiInterface<CategoryModel>> {
    return this.http
      .post<ObjectApiInterface<CategoryModel>>(`${this.baseUrlApi}/${supplierId}/createFamille`, famille)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          entity.famillies.push(x.data);
          this._entity.next(entity);
          return x;
        }),
      );
  }

  deleteFamille(id: string, familleId: string): Observable<ObjectApiInterface<CategoryModel>> {
    return this.http
      .delete<ObjectApiInterface<CategoryModel>>(`${this.baseUrlApi}/${id}/deleteFamille/${familleId}`)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const findedIdx = entity.famillies.findIndex(y => y._id === familleId);
          if (findedIdx !== -1) {
            entity.famillies.splice(findedIdx, 1);
            this._entity.next(entity);
          }
          return x;
        }),
      );
  }

  updateFamille(id: string, data: any): Observable<ObjectApiInterface<SupplierModel>> {
    return this.http
      .put<ObjectApiInterface<SupplierModel>>(`${this.baseUrlApi}/${id}/updateFamille/${data._id}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  findAllClientNumbers(
    supplierId: string,
    page?: number,
    perPage?: number,
    sortField: string = 'clientNumber',
    sortOrder: string = 'asc',
    search?: string,
    filters: any = {},
  ) {
    if (!filters?.isArchive) {
      filters.isArchive = false;
    }
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/${supplierId}/clientNumbers/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  createClientNumber(supplierId: string, data: any): Observable<ObjectApiInterface<ClientNumberModel>> {
    return this.http
      .post<ObjectApiInterface<ClientNumberModel>>(`${this.baseUrlApi}/${supplierId}/createClientNumber`, data)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();

          if (entity) {
            if (!entity.clientNumbers) {
              entity.clientNumbers = [];
            }
            entity.clientNumbers.push(x.data);
            this._entity.next(entity);
          }

          this._entity.next(entity);
          return x;
        }),
      );
  }

  deleteClientNumber(id: string, codeId: string): Observable<ObjectApiInterface<SupplierModel>> {
    return this.http
      .delete<ObjectApiInterface<SupplierModel>>(`${this.baseUrlApi}/${id}/clientNumbers/${codeId}/archive`)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const findedIdx = entity.clientNumbers.findIndex(y => y._id === codeId);
          if (findedIdx !== -1) {
            entity.clientNumbers.splice(findedIdx, 1);
            this._entity.next(entity);
          }
          return x;
        }),
      );
  }

  updateClientNumber(id: string, data: any): Observable<ObjectApiInterface<SupplierModel>> {
    return this.http
      .put<ObjectApiInterface<SupplierModel>>(`${this.baseUrlApi}/${id}/updateClientNumber/${data._id}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  import(entity: SupplierModel): Observable<ObjectApiInterface<SupplierModel>> {
    return super.import(entity);
  }

  importContact(contacts: any, supplierId: string): Observable<any> {
    return this.http
      .post<ObjectApiInterface<SupplierModel>>(`${this.baseUrlApi}/import-contact/${supplierId}`, contacts)
      .pipe(
        map(x => {
          const contacts = this._contacts.value;
          const newContacts = x.data.contacts?.filter(contact => !contacts.map(c => c._id).includes(contact._id));
          this._contacts.value.push(...newContacts);
          return x;
        }),
      );
  }

  getEstablishmentsMissingContacts(supplierId: string): Observable<ObjectApiInterface<EstablishmentMissingContacts[]>> {
    return this.http.get<ObjectApiInterface<EstablishmentMissingContacts[]>>(
      `${this.baseUrlApi}/${supplierId}/establishments-missing-contacts`,
    );
  }

  setContacts(contacts: SupplierContactModel[], emitEntity: boolean = false) {
    const entity = this._entity.getValue();
    if (!entity) return;
    entity.contacts = contacts;
    if (emitEntity) {
      this._entity.next(entity);
    }
    this._contacts.next(contacts);
  }
}
